import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Search, MapPin, Calendar, Clock, Users, Filter, 
  ChevronLeft, User, LogOut, LayoutDashboard, Map, List, X, Heart, BadgeCheck, Shield, Sparkles, Shirt
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { SessionsMap } from '../components/LeafletMap';
import FavoriteButton from '../components/FavoriteButton';
import VerifiedBadge from '../components/VerifiedBadge';
import SessionSidePanel from '../components/SessionSidePanel';
import LevelFilter, { getSessionCountsByLevel } from '../components/LevelFilter';
import DateFilter, { getSessionCountsByDate, matchesDateFilter, DateBadge, StartsInBadge, sortSessionsByTime } from '../components/DateFilter';
import ObjectiveSelector, { filterSessionsByObjective, sortSessionsByObjective } from '../components/ObjectiveSelector';
import OnboardingModal from '../components/OnboardingModal';
import { HeaderLogo } from '../components/AnimatedLogo';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';
import LanguageSelector from '../components/LanguageSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default activity images - fallback when session has no image_url
const defaultActivityImages = {
  yoga: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/1e452f8c4598b8876569e5d022232b19096e8b20700c20fcfb52976365aa16b4.png",
  renforcement: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/0d56e8109c57dea73ff0247cbdfe6bf7daa9936e4867d692c7edcc2f26f978c8.png",
  mobilite: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/5361f451fa5ae6ca0e92d87b059d435275e8c54bf903370e85625d6a121a4cf0.png",
  outdoor: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/e710bd7b10eae3ae767193a7a72bef903236a30fabcba0542001a8462535d563.png",
  running: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/5262e0975539ef5034f608d090b8afb775d3df23f0787b651dfa3f0e5ee50556.png",
  cardio: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/5dc4bfd211de1a8834ce8815afca48579c4f338a4c9d8962f8a62493571867b5.png",
  pilates: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/c890ea7b0d0ea0945befd9b6fc31ea74727a26f0e3134cbac28972cb260da623.png",
  boxe: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/969da96aa4bcb2d93d3dede512e06776827dc9eec58a4e86a5d6ead81b62383c.png",
  danse: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/afe1446a1ae5f2b55cf4dcb64778a73f1e2f15111f92f593454d95b8951636ea.png",
  padel: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/571d1cd80825335f0fa66a750009986c0934a4592d1791c4bf12163eaa53866e.png",
  tennis: "https://static.prod-images.emergentagent.com/jobs/f4b6a95a-7e98-4d45-9f7a-ada39d42bdee/images/889b4402287ed32da011aefc961a00573e9ecbe172b8f626ffec360b21b34810.png",
};

// Helper: get session image with fallback
const getSessionImage = (session) => session.image_url || defaultActivityImages[session.category] || null;

const categoryIcons = {
  yoga: { icon: '🧘', color: '#8b5cf6' },
  renforcement: { icon: '💪', color: '#ef4444' },
  mobilite: { icon: '🌿', color: '#22c55e' },
  outdoor: { icon: '🏔️', color: '#3b82f6' },
  running: { icon: '🏃', color: '#f97316' },
  cardio: { icon: '🔥', color: '#ef4444' },
  pilates: { icon: '🤸', color: '#ec4899' },
  boxe: { icon: '🥊', color: '#f97316' },
  danse: { icon: '💃', color: '#a855f7' },
  padel: { icon: '🏸', color: '#06b6d4' },
  tennis: { icon: '🎾', color: '#84cc16' },
};

const categoriesDef = [
  { id: 'all', nameKey: 'categories.all' },
  { id: 'yoga', nameKey: 'categories.yoga' },
  { id: 'renforcement', nameKey: 'categories.renforcement' },
  { id: 'mobilite', nameKey: 'categories.mobilite' },
  { id: 'outdoor', nameKey: 'categories.outdoor' },
  { id: 'running', nameKey: 'categories.running' },
  { id: 'cardio', nameKey: 'categories.cardio' },
  { id: 'pilates', nameKey: 'categories.pilates' },
  { id: 'boxe', nameKey: 'categories.boxe' },
  { id: 'danse', nameKey: 'categories.danse' },
  { id: 'padel', nameKey: 'categories.padel' },
  { id: 'tennis', nameKey: 'categories.tennis' },
];

const levelsDef = [
  { id: 'all', nameKey: 'levels.all' },
  { id: 'debutant', nameKey: 'levels.debutant' },
  { id: 'intermediaire', nameKey: 'levels.intermediaire' },
  { id: 'avance', nameKey: 'levels.avance' },
  { id: 'tous', nameKey: 'levels.tous' },
];

const levelColors = {
  debutant: 'level-debutant',
  intermediaire: 'level-intermediaire',
  avance: 'level-avance',
  tous: 'level-tous',
};

export default function ExplorePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, isCoach } = useAuth();
  const { t, lang } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('split'); // 'map', 'list', 'split'
  const [selectedSession, setSelectedSession] = useState(null); // For side panel
  const [showOnboarding, setShowOnboarding] = useState(false); // Onboarding modal
  
  // Filter states
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [level, setLevel] = useState(searchParams.get('level') || 'all');
  const [priceMax, setPriceMax] = useState(searchParams.get('price_max') || '');
  const [coachId, setCoachId] = useState(searchParams.get('coach') || '');
  const [coachName, setCoachName] = useState(searchParams.get('coach_name') || '');
  const [quickLevelFilter, setQuickLevelFilter] = useState('all'); // Quick filter on map
  const [quickDateFilter, setQuickDateFilter] = useState('all'); // Quick date filter on map
  const [selectedObjective, setSelectedObjective] = useState(null); // User objective filter

  // Handle onboarding completion - save to backend if authenticated
  const handleOnboardingComplete = async (objective) => {
    if (objective) {
      setSelectedObjective(objective);
    }
    if (isAuthenticated) {
      try {
        await fetch(`${API_URL}/api/users/onboarding-complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ objective }),
        });
      } catch (err) {
        console.error('Error saving onboarding status:', err);
      }
    }
  };

  // Auto-trigger onboarding for authenticated users who haven't completed it
  useEffect(() => {
    if (isAuthenticated && user && user.onboarding_completed === false) {
      setShowOnboarding(true);
    }
  }, [isAuthenticated, user]);

  // Filter sessions by objective, quick level and date filters
  const filteredSessions = useMemo(() => {
    let filtered = sessions;
    
    // Objective filter (applies level and category filters based on objective)
    if (selectedObjective) {
      filtered = filterSessionsByObjective(filtered, selectedObjective);
      filtered = sortSessionsByObjective(filtered, selectedObjective);
    }
    
    // Level filter (only if no objective is selected, or for additional filtering)
    if (quickLevelFilter !== 'all') {
      filtered = filtered.filter(s => s.level === quickLevelFilter || s.level === 'tous');
    }
    
    // Date filter
    if (quickDateFilter !== 'all') {
      filtered = filtered.filter(s => matchesDateFilter(s.date, quickDateFilter));
    }
    
    // Sort by time (upcoming sessions first)
    filtered = sortSessionsByTime(filtered);
    
    return filtered;
  }, [sessions, selectedObjective, quickLevelFilter, quickDateFilter]);

  // Get counts for level filter badges (based on date-filtered sessions)
  const levelCounts = useMemo(() => {
    const dateFiltered = quickDateFilter === 'all' 
      ? sessions 
      : sessions.filter(s => matchesDateFilter(s.date, quickDateFilter));
    return getSessionCountsByLevel(dateFiltered);
  }, [sessions, quickDateFilter]);

  // Get counts for date filter badges (based on level-filtered sessions)
  const dateCounts = useMemo(() => {
    const levelFiltered = quickLevelFilter === 'all' 
      ? sessions 
      : sessions.filter(s => s.level === quickLevelFilter || s.level === 'tous');
    return getSessionCountsByDate(levelFiltered);
  }, [sessions, quickLevelFilter]);

  useEffect(() => {
    fetchSessions();
  }, [searchParams]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const categoryParam = searchParams.get('category');
      const levelParam = searchParams.get('level');
      const cityParam = searchParams.get('city');
      const priceParam = searchParams.get('price_max');
      const coachParam = searchParams.get('coach');

      if (categoryParam && categoryParam !== 'all') params.set('category', categoryParam);
      if (levelParam && levelParam !== 'all') params.set('level', levelParam);
      if (cityParam) params.set('city', cityParam);
      if (priceParam) params.set('price_max', priceParam);
      if (coachParam) params.set('coach_id', coachParam);

      const response = await fetch(`${API_URL}/api/sessions?${params.toString()}&page_size=500`);
      if (response.ok) {
        const data = await response.json();
        // Handle paginated response
        setSessions(data.items || data);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (category && category !== 'all') params.set('category', category);
    if (level && level !== 'all') params.set('level', level);
    if (city) params.set('city', city);
    if (priceMax) params.set('price_max', priceMax);
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setCity('');
    setCategory('all');
    setLevel('all');
    setPriceMax('');
    setSearchParams(new URLSearchParams());
  };

  const handleCategoryChange = (catId) => {
    setCategory(catId);
    const params = new URLSearchParams(searchParams);
    if (catId === 'all') {
      params.delete('category');
    } else {
      params.set('category', catId);
    }
    setSearchParams(params);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Handle session selection from map - open side panel
  const handleSessionSelect = (session) => {
    setSelectedSession(session);
  };

  // Handle booking from side panel
  const handleBookFromPanel = (sessionId) => {
    navigate(`/session/${sessionId}?book=true`);
  };

  // Close side panel
  const closeSidePanel = () => {
    setSelectedSession(null);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-[1001] glass border-b border-white/10">
        <div className="max-w-[1800px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-white/60 hover:text-white transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <HeaderLogo linkTo="/" size="sm" testId="explore-logo" />
            </div>

            {/* View Mode Toggle */}
            <div className="hidden md:flex items-center gap-1 glass-card p-1 rounded-full">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-full transition-colors ${viewMode === 'list' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
                data-testid="view-list"
              >
                <List className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`p-2 rounded-full transition-colors ${viewMode === 'split' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
                data-testid="view-split"
              >
                <div className="flex gap-0.5">
                  <div className="w-2 h-4 bg-current rounded-sm"></div>
                  <div className="w-2 h-4 bg-current rounded-sm"></div>
                </div>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 rounded-full transition-colors ${viewMode === 'map' ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}
                data-testid="view-map"
              >
                <Map className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <Link to="/outfit">
                    <Button variant="ghost" size="sm" className="text-emerald-400/80 hover:text-emerald-400" data-testid="nav-outfit">
                      <Shirt className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">{t('nav.outfit') || 'Tenue'}</span>
                    </Button>
                  </Link>
                  <Link to="/admin/coach-applications">
                    <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80" data-testid="nav-admin">
                      <Shield className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Admin</span>
                    </Button>
                  </Link>
                  <Link to={isCoach ? "/coach/dashboard" : "/dashboard"}>
                    <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Dashboard</span>
                    </Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />
                  </Button>
                </>
              ) : (
                <Link to="/login">
                  <Button className="btn-primary py-2 px-5" data-testid="nav-login">
                    {t('nav.login')}
                  </Button>
                </Link>
              )}
              <LanguageSelector />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto">
        <div className={`flex ${viewMode === 'list' ? 'flex-col' : 'flex-col lg:flex-row'}`}>
          
          {/* Filters & List Panel */}
          <div className={`${viewMode === 'map' ? 'hidden lg:block lg:w-[450px]' : viewMode === 'split' ? 'w-full lg:w-1/2' : 'w-full'} p-4 lg:p-6 lg:h-[calc(100vh-80px)] lg:overflow-y-auto scrollbar-hide`}>
            
            {/* Objective Selector */}
            <ObjectiveSelector 
              onObjectiveChange={setSelectedObjective}
              initialObjective={selectedObjective}
              className="mb-4 p-4 glass-card rounded-2xl"
            />
            
            {/* Personalize Experience Button */}
            <motion.button
              onClick={() => setShowOnboarding(true)}
              className="w-full mb-6 p-3 rounded-xl bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/30 hover:border-primary/50 transition-all flex items-center justify-center gap-2 text-sm group"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              data-testid="personalize-experience-btn"
            >
              <Sparkles className="w-4 h-4 text-primary group-hover:animate-pulse" />
              <span className="text-white/70 group-hover:text-white">{t('explore.personalize')}</span>
            </motion.button>
            
            {/* Search and Filters */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                {t('explore.findActivity')}
              </h2>
              
              {/* Coach filter indicator */}
              {coachId && coachName && (
                <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-cyan-400" />
                    <span className="text-cyan-300 text-sm">
                      {t('explore.filteringByCoach') || 'Séances de'}: <strong>{decodeURIComponent(coachName)}</strong>
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-cyan-400 hover:text-white hover:bg-cyan-500/20 h-7 px-2"
                    onClick={() => {
                      setCoachId('');
                      setCoachName('');
                      const newParams = new URLSearchParams(searchParams);
                      newParams.delete('coach');
                      newParams.delete('coach_name');
                      setSearchParams(newParams);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <form onSubmit={handleFilterSubmit} className="space-y-4">
                {/* Main search bar */}
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30 pointer-events-none" />
                    <Input
                      type="text"
                      placeholder={t('explore.cityPlaceholder')}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="input-modern pl-12 h-14"
                      data-testid="filter-city"
                      list="brussels-communes"
                    />
                    <datalist id="brussels-communes">
                      <option value="1000 - Bruxelles Centre" />
                      <option value="1030 - Schaerbeek" />
                      <option value="1040 - Etterbeek" />
                      <option value="1050 - Ixelles" />
                      <option value="1060 - Saint-Gilles" />
                      <option value="1070 - Anderlecht" />
                      <option value="1080 - Molenbeek" />
                      <option value="1081 - Koekelberg" />
                      <option value="1082 - Berchem-Sainte-Agathe" />
                      <option value="1083 - Ganshoren" />
                      <option value="1090 - Jette" />
                      <option value="1140 - Evere" />
                      <option value="1150 - Woluwe-Saint-Pierre" />
                      <option value="1160 - Auderghem" />
                      <option value="1170 - Watermael-Boitsfort" />
                      <option value="1180 - Uccle" />
                      <option value="1190 - Forest" />
                      <option value="1200 - Woluwe-Saint-Lambert" />
                      <option value="1210 - Saint-Josse" />
                    </datalist>
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="h-14 px-4 border-white/10 hover:bg-white/10"
                    onClick={() => setShowFilters(!showFilters)}
                    data-testid="toggle-filters"
                  >
                    <Filter className="w-5 h-5" />
                  </Button>
                  <Button type="submit" className="btn-primary h-14 px-6" data-testid="apply-filters">
                    <Search className="w-5 h-5" />
                  </Button>
                </div>

                {/* Extended filters */}
                <AnimatePresence>
                  {showFilters && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-5 glass-card rounded-2xl">
                        <div>
                          <label className="text-sm text-white/50 mb-2 block">{t('explore.category')}</label>
                          <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="filter-category" className="bg-black/20 border-white/10">
                              <SelectValue placeholder={t('explore.category')} />
                            </SelectTrigger>
                            <SelectContent>
                              {categoriesDef.map((cat) => (
                                <SelectItem key={cat.id} value={cat.id}>{t(cat.nameKey)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm text-white/50 mb-2 block">{t('explore.level')}</label>
                          <Select value={level} onValueChange={setLevel}>
                            <SelectTrigger data-testid="filter-level" className="bg-black/20 border-white/10">
                              <SelectValue placeholder={t('explore.level')} />
                            </SelectTrigger>
                            <SelectContent>
                              {levelsDef.map((lvl) => (
                                <SelectItem key={lvl.id} value={lvl.id}>{t(lvl.nameKey)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm text-white/50 mb-2 block">{t('explore.priceMax')}</label>
                          <Input
                            type="number"
                            placeholder="Ex: 25"
                            value={priceMax}
                            onChange={(e) => setPriceMax(e.target.value)}
                            className="bg-black/20 border-white/10"
                            data-testid="filter-price"
                          />
                        </div>
                        <div className="sm:col-span-3 flex justify-between items-center pt-2">
                          <Button type="button" variant="ghost" onClick={clearFilters} className="text-white/50 hover:text-white" data-testid="clear-filters">
                            <X className="w-4 h-4 mr-2" />
                            {t('explore.clear')}
                          </Button>
                          <Button type="submit" className="btn-primary py-2">
                            {t('explore.apply')}
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>

              {/* Category Pills */}
              <div className="flex gap-2 overflow-x-auto pb-2 mt-4 scrollbar-hide">
                {categoriesDef.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`category-pill whitespace-nowrap ${
                      (searchParams.get('category') || 'all') === cat.id ? 'active' : ''
                    }`}
                    data-testid={`pill-${cat.id}`}
                  >
                    {t(cat.nameKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Results */}
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-bold uppercase">
                {loading ? t('explore.loading') : `${filteredSessions.length} ${filteredSessions.length !== 1 ? t('explore.sessions') : t('explore.session')}`}
              </h2>
              {(quickLevelFilter !== 'all' || quickDateFilter !== 'all') && (
                <button 
                  onClick={() => { setQuickLevelFilter('all'); setQuickDateFilter('all'); }}
                  className="text-sm text-[var(--brand-cyan)] hover:underline flex items-center gap-1"
                  data-testid="reset-quick-filters"
                >
                  <X className="w-3 h-3" />
                  {t('explore.resetFilters')}
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : filteredSessions.length === 0 ? (
              <div className="text-center py-16 glass-card rounded-2xl">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold mb-2">{t('explore.noResults')}</h3>
                <p className="text-white/50 mb-6">
                  {t('explore.noResultsDesc')}
                </p>
                <Button onClick={() => { clearFilters(); setQuickLevelFilter('all'); setQuickDateFilter('all'); }} variant="outline" className="border-white/20" data-testid="no-results-clear">
                  {t('explore.clearFilters')}
                </Button>
              </div>
            ) : (
              <motion.div 
                initial="hidden"
                animate="visible"
                variants={{
                  visible: { transition: { staggerChildren: 0.03 } }
                }}
                className={viewMode === 'list' ? 'grid-sessions' : 'space-y-4'}
              >
                {filteredSessions.slice(0, 50).map((session) => (
                  <motion.div
                    key={session.session_id}
                    variants={fadeInUp}
                  >
                    <div className="session-card card-hover block relative overflow-hidden rounded-2xl">
                      {/* Session image */}
                      {getSessionImage(session) && (
                        <div className="relative h-36 w-full overflow-hidden">
                          <img 
                            src={getSessionImage(session)} 
                            alt={session.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            data-testid={`session-image-${session.session_id}`}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#12131a] via-transparent to-transparent" />
                          {/* Category icon overlay */}
                          {categoryIcons[session.category] && (
                            <div 
                              className="absolute top-3 left-3 w-9 h-9 rounded-xl flex items-center justify-center text-base backdrop-blur-sm"
                              style={{ backgroundColor: `${categoryIcons[session.category].color}30` }}
                              data-testid={`session-category-icon-${session.session_id}`}
                            >
                              {categoryIcons[session.category].icon}
                            </div>
                          )}
                          {/* Favorite button overlay */}
                          <div className="absolute top-3 right-3 z-10">
                            <FavoriteButton sessionId={session.session_id} size="small" />
                          </div>
                        </div>
                      )}
                      {/* Fallback: no image */}
                      {!getSessionImage(session) && (
                        <>
                          {categoryIcons[session.category] && (
                            <div 
                              className="absolute top-4 left-4 w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                              style={{ backgroundColor: `${categoryIcons[session.category].color}15` }}
                              data-testid={`session-category-icon-${session.session_id}`}
                            >
                              {categoryIcons[session.category].icon}
                            </div>
                          )}
                          <div className="absolute top-4 right-4 z-10">
                            <FavoriteButton sessionId={session.session_id} size="small" />
                          </div>
                        </>
                      )}
                      
                      <Link
                        to={`/session/${session.session_id}`}
                        data-testid={`session-card-${session.session_id}`}
                      >
                        <div className={`${getSessionImage(session) ? 'p-4' : 'p-5'}`}>
                          <div className={`flex justify-between items-start mb-3 ${getSessionImage(session) ? '' : 'pl-12 pr-10'}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${levelColors[session.level] || 'level-tous'}`}>
                                {t(`levels.${session.level}`) || session.level}
                              </span>
                              {session.is_event && (
                                <span className="badge-event">{t('explore.event')}</span>
                              )}
                              <DateBadge date={session.date} />
                              <StartsInBadge date={session.date} startTime={session.start_time} />
                            </div>
                            <span className={session.status === 'confirmed' ? 'badge-confirmed' : 'badge-pending'}>
                              {session.status === 'confirmed' ? t('explore.confirmed') : t('explore.pending')}
                            </span>
                          </div>

                          <div className={getSessionImage(session) ? '' : 'pl-12'}>
                            <h3 className="font-bold text-lg mb-1 line-clamp-1">{session.title}</h3>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full mb-2 inline-block" style={{ color: categoryIcons[session.category]?.color || '#3BADE5', backgroundColor: `${categoryIcons[session.category]?.color || '#3BADE5'}15` }}>
                              {t(`categories.${session.category}`) || session.category}
                            </span>
                          </div>
                          
                          <div className={`space-y-2 text-sm ${getSessionImage(session) ? '' : 'pl-12'}`}>
                            <div className="flex items-center gap-2 text-white/60">
                              <User className="w-4 h-4 text-[var(--brand-cyan)]" />
                              <span>{session.coach_name || 'Coach'}</span>
                              {session.coach_verified && (
                                <VerifiedBadge isVerified={true} size="small" />
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-white/60">
                              <MapPin className="w-4 h-4 text-[var(--brand-green)]" />
                              <span className="line-clamp-1">{session.location}</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/60">
                              <Calendar className="w-4 h-4 text-[var(--brand-yellow)]" />
                              <span>{formatDate(session.date)}</span>
                              <Clock className="w-4 h-4 ml-2" />
                              <span>{session.start_time}</span>
                            </div>
                          </div>

                          {/* Participant Avatars */}
                          {session.participant_avatars && session.participant_avatars.length > 0 && (
                            <div className="mt-3 flex items-center gap-2">
                              <div className="flex -space-x-2">
                                {session.participant_avatars.slice(0, 4).map((participant, idx) => (
                                  <div 
                                    key={idx}
                                    className="w-7 h-7 rounded-full border-2 border-[var(--dark-bg)] overflow-hidden bg-gray-600"
                                    title={participant.name}
                                  >
                                    {participant.picture ? (
                                      <img src={participant.picture} alt={participant.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-xs text-white font-medium bg-gradient-to-br from-cyan-500 to-blue-500">
                                        {participant.name?.charAt(0)?.toUpperCase() || '?'}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                {session.current_participants > 4 && (
                                  <div className="w-7 h-7 rounded-full border-2 border-[var(--dark-bg)] bg-gray-700 flex items-center justify-center text-xs text-white font-medium">
                                    +{session.current_participants - 4}
                                  </div>
                                )}
                              </div>
                              <span className="text-xs text-white/50">
                                {session.current_participants} inscrit{session.current_participants > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}

                          <div className={`mt-4 pt-4 border-t border-white/10 flex justify-between items-center ${getSessionImage(session) ? '' : 'pl-12'}`}>
                            <span className="text-2xl font-bold text-[var(--brand-cyan)]">{session.price}€</span>
                            <span className="text-sm text-white/40">{session.duration_minutes} {t('explore.min')}</span>
                          </div>
                        </div>
                      </Link>
                    </div>
                  </motion.div>
                ))}
                {filteredSessions.length > 50 && (
                  <p className="text-center text-white/40 py-4">
                    Affichage des 50 premières séances sur {filteredSessions.length}
                  </p>
                )}
              </motion.div>
            )}
          </div>

          {/* Map Panel */}
          {viewMode !== 'list' && (
            <div className={`${viewMode === 'map' ? 'w-full' : 'hidden lg:block lg:w-1/2'} h-[50vh] lg:h-[calc(100vh-80px)] lg:sticky lg:top-20 relative`}>
              {/* Quick Filters - floating on map, centered */}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex flex-col items-center gap-2">
                {/* Level Filter */}
                <LevelFilter
                  selectedLevel={quickLevelFilter}
                  onLevelChange={setQuickLevelFilter}
                  counts={levelCounts}
                  variant="inline"
                  className="bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 p-1.5"
                />
                
                {/* Date Filter */}
                <DateFilter
                  selectedDate={quickDateFilter}
                  onDateChange={setQuickDateFilter}
                  counts={dateCounts}
                />
              </div>
              
              <SessionsMap 
                sessions={filteredSessions}
                onSessionSelect={handleSessionSelect}
                className="w-full h-full"
                showUserLocation={true}
                showRadius={true}
                radiusKm={2}
              />
              
              {/* Session Side Panel - overlays the map */}
              {selectedSession && (
                <SessionSidePanel 
                  session={selectedSession}
                  onClose={closeSidePanel}
                  onBook={handleBookFromPanel}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile View Toggle */}
      <div className="lg:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000]">
        <div className="glass-heavy flex items-center gap-1 p-1.5 rounded-full">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-full transition-all ${viewMode === 'list' ? 'bg-[var(--brand-cyan)] text-white' : 'text-white/60'}`}
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('map')}
            className={`px-4 py-2 rounded-full transition-all ${viewMode === 'map' ? 'bg-[var(--brand-cyan)] text-white' : 'text-white/60'}`}
          >
            <Map className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Onboarding Modal */}
      <OnboardingModal 
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={handleOnboardingComplete}
      />
    </div>
  );
}
